import React, { useContext, useState } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Alert,
  Tabs,
  Tab,
  Divider
} from '@mui/material';
import { UserContext } from '../components/MiniDrawer';
import SecurityIcon from '@mui/icons-material/Security';
import StorageIcon from '@mui/icons-material/Storage';
import InfoIcon from '@mui/icons-material/Info';
import SettingsIcon from '@mui/icons-material/Settings';

export default function Profile() {
  const { cognitoUser, backendUser, isAuthenticated, authLoading } = useContext(UserContext);
  const [currentTab, setCurrentTab] = useState(0);

  if (authLoading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6">🔄 Loading authentication data...</Typography>
      </Box>
    );
  }

  if (!isAuthenticated || !cognitoUser) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="h6">❌ User not authenticated</Typography>
          <Typography>Please login to view profile information.</Typography>
        </Alert>
      </Box>
    );
  }

  const cognitoUserDetails = [
    { label: 'Email', value: cognitoUser.email },
    { label: 'Name', value: cognitoUser.name },
    { label: 'Surname', value: cognitoUser.surname },
    { label: 'Username', value: cognitoUser.username },
    { label: 'Cognito Sub', value: cognitoUser.sub },
    { label: 'Groups', value: cognitoUser.groups?.join(', ') || 'None' },
  ];

  const backendUserDetails = backendUser ? [
    { label: 'Backend ID', value: backendUser.id },
    { label: 'Username', value: backendUser.username },
    { label: 'Email', value: backendUser.email },
    { label: 'Role', value: backendUser.role?.name || backendUser.role },
    { label: 'Role Description', value: backendUser.role?.description || 'Not provided' },
    { label: 'People Manager', value: backendUser.people_manager?.username || 'Not assigned' },
    { label: 'People Manager Email', value: backendUser.people_manager?.email || 'Not provided' },
  ] : [];

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  // TabPanel component helper
  function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
      <Box
        role="tabpanel"
        hidden={value !== index}
        id={`profile-tabpanel-${index}`}
        aria-labelledby={`profile-tab-${index}`}
        {...other}
      >
        {value === index && (
          <Box sx={{ p: 4 }}>
            {children}
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box>
      <Box my={4}>
        {/* Header */}
        <Box display="flex" alignItems="center" mb={3}>
          <Typography variant="h4" sx={{ fontSize: '1.5rem', flexGrow: 1 }}>
            User Profile:
            <Box
              component="span"
              sx={{
                display: { xs: 'block', md: 'inline' },
                bgcolor: '#e0e0e0',
                color: '#607c89',
                fontWeight: 500,
                fontSize: '0.9em',
                borderRadius: 1,
                px: 1.5,
                py: 0.5,
                ml: { xs: 0, md: 1 },
                mt: { xs: 1, md: 0 }
              }}
            >
              {cognitoUser.email}
            </Box>
          </Typography>
        </Box>

        {/* User Information */}
        <Card>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ maxWidth: '600px' }}>
              {!backendUser && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  <Typography variant="h6">⚠️ Backend User Not Found</Typography>
                  <Typography>
                    User not found in backend database. Only Cognito authentication data is available.
                  </Typography>
                </Alert>
              )}

              {/* Backend User Information */}
              {backendUser && (
                <Box>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableBody>
                        {backendUserDetails.map((detail, index) => (
                          <TableRow key={index}>
                            <TableCell sx={{ 
                              fontWeight: 'bold',
                              width: '200px',
                              minWidth: '200px',
                              color: 'rgba(0, 0, 0, 0.6)'
                            }}>
                              {detail.label}
                            </TableCell>
                            <TableCell sx={{ 
                              borderLeft: '1px solid #e0e0e0',
                              pl: 2
                            }}>
                              {detail.value || 'Not provided'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
} 